import { BaseElement } from '../../base-element';
import { Signal } from 'signal-polyfill';
import { adminApi } from '../../../api/admin.api';
import type { TrialEventSummaryDto } from '../../../api/types';
import { authState, canManageTrialEvents } from '../../../state/auth.state';
import { navigate } from '../../../router';
import './secretary-entry-list-view';
import '../../ui/dt-badge';
import '../../ui/dt-button';
import '../../ui/dt-card';
import '../../ui/dt-input';
import '../../ui/dt-select';
import '../../ui/dt-spinner';

type PageState = 'loading' | 'loaded' | 'empty' | 'error' | 'forbidden';

interface TrialEventFilters {
  search: string;
  organizationCode: string;
  sportCode: string;
  dateFrom: string;
  dateTo: string;
  includeInactive: boolean;
}

type InputElement = HTMLElement & {
  value: string;
};

type DtButtonElement = HTMLElement & {
  disabled?: boolean;
  loading?: boolean;
};

type DtSelectElement = HTMLElement & {
  options: Array<{ value: string; label: string }>;
  value: string;
  disabled?: boolean;
};

type SecretaryEntryListViewElement = HTMLElement & {
  setAttribute(name: 'trial-event-id' | 'trial-name', value: string): void;
};

const defaultFilters = (): TrialEventFilters => ({
  search: '',
  organizationCode: '',
  sportCode: '',
  dateFrom: '',
  dateTo: '',
  includeInactive: false,
});

export class SecretaryListPage extends BaseElement {
  private trialEvents = new Signal.State<TrialEventSummaryDto[]>([]);
  private loading = new Signal.State(false);
  private error = new Signal.State<string | null>(null);
  private page = new Signal.State(1);
  private pageSize = new Signal.State(10);
  private total = new Signal.State(0);
  private initialized = false;

  private activeFilters: TrialEventFilters = defaultFilters();
  private draftFilters: TrialEventFilters = defaultFilters();

  private pageState = new Signal.Computed<PageState>(() => {
    const auth = authState.get();
    if (!auth.isAuthenticated || !canManageTrialEvents.get()) {
      return 'forbidden';
    }

    if (this.isEntryWorkflowView()) {
      return 'loaded';
    }

    if (this.loading.get()) return 'loading';
    if (this.error.get()) return 'error';
    if (this.trialEvents.get().length === 0) return 'empty';
    return 'loaded';
  });

  connectedCallback(): void {
    this.watch(this.trialEvents, () => this.updateRender());
    this.watch(this.loading, () => this.updateRender());
    this.watch(this.error, () => this.updateRender());
    this.watch(this.page, () => this.updateRender());
    this.watch(this.pageSize, () => this.updateRender());
    this.watch(this.total, () => this.updateRender());
    this.watch(authState, () => this.ensureData());
    this.watch(canManageTrialEvents, () => this.ensureData());
    this.updateRender();
    this.ensureData();
  }

  private canAccess(): boolean {
    const auth = authState.get();
    return auth.isAuthenticated && canManageTrialEvents.get();
  }

  private getSelectedTrialEventId(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('trialEventId');
  }

  private getSelectedTrialName(): string {
    const params = new URLSearchParams(window.location.search);
    return params.get('trialName') ?? '';
  }

  private isEntryWorkflowView(): boolean {
    return !!this.getSelectedTrialEventId();
  }

  private ensureData(): void {
    if (!this.canAccess() || this.initialized || this.isEntryWorkflowView()) {
      return;
    }

    this.initialized = true;
    void this.loadTrialEvents();
  }

  private buildListParams() {
    return {
      includeInactive: this.activeFilters.includeInactive,
      search: this.activeFilters.search.trim() || undefined,
      organizationCode: this.activeFilters.organizationCode.trim() || undefined,
      sportCode: this.activeFilters.sportCode.trim() || undefined,
      dateFrom: this.activeFilters.dateFrom || undefined,
      dateTo: this.activeFilters.dateTo || undefined,
      page: this.page.get(),
      pageSize: this.pageSize.get(),
    };
  }

  private async loadTrialEvents(): Promise<void> {
    if (!this.canAccess() || this.isEntryWorkflowView()) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const response = await adminApi.trialEvents.list(this.buildListParams());
    if (response.error) {
      this.trialEvents.set([]);
      this.total.set(0);
      this.error.set(response.error.title || 'Unable to load trial events.');
      this.loading.set(false);
      return;
    }

    const data = response.data;
    this.trialEvents.set(data?.items ?? []);
    this.total.set(data?.total ?? 0);
    this.page.set(data?.page ?? this.page.get());
    this.pageSize.set(data?.pageSize ?? this.pageSize.get());
    this.loading.set(false);
  }

  private getTotalPages(): number {
    const pages = Math.ceil(this.total.get() / this.pageSize.get());
    return pages > 0 ? pages : 1;
  }

  private handleSearchInput = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.draftFilters.search = detail?.value ?? '';
  };

  private handleOrganizationInput = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.draftFilters.organizationCode = detail?.value ?? '';
  };

  private handleSportInput = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.draftFilters.sportCode = detail?.value ?? '';
  };

  private handleDateFromInput = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.draftFilters.dateFrom = detail?.value ?? '';
  };

  private handleDateToInput = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    this.draftFilters.dateTo = detail?.value ?? '';
  };

  private handleIncludeInactiveChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    this.draftFilters.includeInactive = target.checked;
  };

  private handleApplyFilters = (): void => {
    this.activeFilters = { ...this.draftFilters };
    this.page.set(1);
    void this.loadTrialEvents();
  };

  private handleClearFilters = (): void => {
    this.activeFilters = defaultFilters();
    this.draftFilters = defaultFilters();
    this.page.set(1);
    this.updateRender();
    void this.loadTrialEvents();
  };

  private handleRetry = (): void => {
    void this.loadTrialEvents();
  };

  private handlePrevPage = (): void => {
    if (this.page.get() <= 1) {
      return;
    }

    this.page.set(this.page.get() - 1);
    void this.loadTrialEvents();
  };

  private handleNextPage = (): void => {
    if (this.page.get() >= this.getTotalPages()) {
      return;
    }

    this.page.set(this.page.get() + 1);
    void this.loadTrialEvents();
  };

  private handlePageSizeChange = (event: Event): void => {
    const detail = (event as CustomEvent<{ value: string }>).detail;
    const nextSize = Number(detail?.value);
    if (Number.isNaN(nextSize) || nextSize <= 0) {
      return;
    }

    this.pageSize.set(nextSize);
    this.page.set(1);
    void this.loadTrialEvents();
  };

  private handleOpenTrialEvent = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const trialEventId = target.getAttribute('data-trial-event-id');
    if (!trialEventId) {
      return;
    }

    navigate(`/admin/trial-events/${trialEventId}`);
  };

  private handleManageEntries = (event: Event): void => {
    const target = event.currentTarget as HTMLElement;
    const trialEventId = target.getAttribute('data-trial-event-id');
    const trialName = target.getAttribute('data-trial-name') ?? '';
    if (!trialEventId) {
      return;
    }

    const params = new URLSearchParams();
    params.set('trialEventId', trialEventId);
    if (trialName) {
      params.set('trialName', trialName);
    }

    navigate(`/secretary?${params.toString()}`);
  };

  private renderLoading(): string {
    return `
      <div class="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_48px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark" data-testid="loading-state">
        <div class="pointer-events-none absolute h-36 w-36 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/20"></div>
        <div class="relative flex min-h-[300px] flex-col items-center justify-center gap-4 px-6 text-center">
          <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Secretary portal</div>
          <dt-spinner size="large"></dt-spinner>
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Loading trial events</h2>
            <p class="text-sm text-neutral-600 dark:text-neutral-300">We’re preparing the management view and applying your current filters.</p>
          </div>
        </div>
      </div>
    `;
  }

  private renderError(message: string): string {
    return `
      <div class="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_48px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark" data-testid="error-state">
        <div class="pointer-events-none absolute h-36 w-36 rounded-full bg-error-50 blur-3xl dark:bg-error-500/10"></div>
        <div class="relative flex min-h-[260px] flex-col items-center justify-center gap-4 px-6 text-center">
          <div class="flex h-16 w-16 items-center justify-center rounded-full bg-error-50 text-error-500 ring-8 ring-white dark:bg-error-500/10 dark:ring-surface-dark">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div class="space-y-2">
            <h2 class="text-2xl font-semibold text-neutral-900 dark:text-white">Unable to load trial events</h2>
            <p class="max-w-md text-sm text-neutral-600 dark:text-neutral-300">${message}</p>
          </div>
          <dt-button variant="outline" class="mt-2" data-testid="retry-btn">Retry</dt-button>
        </div>
      </div>
    `;
  }

  private renderEmpty(): string {
    return `
      <div class="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_48px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark" data-testid="empty-state">
        <div class="pointer-events-none absolute h-36 w-36 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/20"></div>
        <div class="relative flex min-h-[240px] flex-col items-center justify-center gap-3 px-6 text-center">
          <div class="inline-flex items-center rounded-full bg-secondary-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-secondary-700 dark:bg-secondary-500/15 dark:text-secondary-200">No active matches</div>
          <p class="text-2xl font-semibold text-neutral-900 dark:text-white">No trial events matched these filters.</p>
          <p class="max-w-xl text-sm leading-7 text-neutral-600 dark:text-neutral-300">Try widening the date window, removing a code filter, or include inactive trial events.</p>
        </div>
      </div>
    `;
  }

  private renderPagination(): string {
    if (this.total.get() <= this.pageSize.get()) {
      return '';
    }

    const start = this.total.get() === 0 ? 0 : (this.page.get() - 1) * this.pageSize.get() + 1;
    const end = Math.min(this.page.get() * this.pageSize.get(), this.total.get());

    return `
      <div class="flex flex-wrap items-center justify-between gap-4 border-t border-neutral-200 px-4 py-4" data-testid="pagination">
        <div class="text-xs text-neutral-500">Showing ${start}-${end} of ${this.total.get()}</div>
        <div class="flex flex-wrap items-center gap-3">
          <dt-button variant="outline" data-testid="prev-page">Previous</dt-button>
          <span class="text-xs font-semibold text-neutral-600">Page ${this.page.get()} of ${this.getTotalPages()}</span>
          <dt-button variant="outline" data-testid="next-page">Next</dt-button>
        </div>
        <div class="min-w-[120px]">
          <dt-select label="Page size" data-testid="page-size"></dt-select>
        </div>
      </div>
    `;
  }

  private renderTrialEventsTable(): string {
    return `
      <div class="overflow-hidden rounded-[28px] border border-neutral-200 bg-white shadow-[0_18px_48px_rgba(48,52,37,0.08)] dark:border-white/10 dark:bg-surface-dark" data-testid="trial-event-table">
        <div class="grid grid-cols-1 gap-3 border-b border-neutral-200 bg-[linear-gradient(135deg,#fff8ef_0%,#f7f1e3_100%)] px-4 py-4 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:border-white/10 dark:bg-[linear-gradient(135deg,rgba(26,29,20,0.96)_0%,rgba(30,26,24,0.94)_100%)] lg:grid-cols-[2fr_1.2fr_1fr_0.8fr_1.4fr] lg:gap-4">
          <div>Trial Event</div>
          <div>Host Club</div>
          <div>Dates</div>
          <div>Status</div>
          <div>Actions</div>
        </div>
        <div class="divide-y divide-neutral-200">
          ${this.trialEvents
            .get()
            .map(
              (trialEvent) => `
            <div class="grid grid-cols-1 gap-4 px-4 py-5 lg:grid-cols-[2fr_1.2fr_1fr_0.8fr_1.4fr] lg:items-center" data-testid="trial-event-row" data-trial-event-id="${this.esc(trialEvent.trialEventId)}">
              <div class="space-y-2">
                <div class="text-sm font-semibold text-neutral-900">${this.esc(trialEvent.name)}</div>
                <div class="flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                  <span>${this.esc(trialEvent.formTemplate.organizationCode || '—')}</span>
                  <span>&middot;</span>
                  <span>${this.esc(trialEvent.formTemplate.sportCode || '—')}</span>
                  <span>&middot;</span>
                  <span>${this.esc(trialEvent.trackingSlug || 'No tracking slug')}</span>
                </div>
              </div>
              <div class="text-sm text-neutral-700">
                <div>${this.esc(trialEvent.hostClubName)}</div>
                <div class="mt-1 text-xs text-neutral-500">${this.esc(trialEvent.secretaryEmail || 'No secretary email')}</div>
              </div>
              <div class="text-sm text-neutral-700">
                <div>${this.esc(trialEvent.startDate)}</div>
                <div class="text-xs text-neutral-500">to ${this.esc(trialEvent.endDate)}</div>
              </div>
              <div>
                <dt-badge variant="${trialEvent.isActive ? 'success' : 'warning'}">${trialEvent.isActive ? 'Active' : 'Inactive'}</dt-badge>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <dt-button
                  variant="outline"
                  data-testid="open-trial-event"
                  data-trial-event-id="${this.esc(trialEvent.trialEventId)}"
                >Open details</dt-button>
                <dt-button
                  variant="ghost"
                  data-testid="manage-entries"
                  data-trial-event-id="${this.esc(trialEvent.trialEventId)}"
                  data-trial-name="${this.esc(trialEvent.name)}"
                >Manage entries</dt-button>
              </div>
            </div>
          `
            )
            .join('')}
        </div>
        ${this.renderPagination()}
      </div>
    `;
  }

  private renderManagementView(): string {
    const state = this.pageState.get();
    let body = '';

    if (state === 'loading') {
      body = this.renderLoading();
    } else if (state === 'error') {
      body = this.renderError(this.error.get() ?? 'Unable to load trial events.');
    } else if (state === 'empty') {
      body = this.renderEmpty();
    } else {
      body = this.renderTrialEventsTable();
    }

    return `
      <section class="px-4 py-6" data-testid="secretary-page">
        <div class="mx-auto flex w-full max-w-6xl flex-col gap-6">
          <dt-card variant="outlined">
            <div slot="header">Trial Event Management</div>
            <div class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div class="max-w-3xl text-sm text-neutral-600">
                Shared landing screen for Secretary and AppAdmin. Active trial events are shown by default, and you can open the admin detail editor or jump into the scoped entry workflow for a specific event.
              </div>
              <a href="/secretary/trial-events/new" class="inline-flex shrink-0 rounded-lg bg-primary-600 px-4 py-3 text-sm font-semibold text-white" data-new-event>New trial event</a>
            </div>
          </dt-card>

          <dt-card variant="outlined">
            <div slot="header">Filters</div>
            <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <dt-input label="Search" placeholder="Event name, host club, or slug" data-testid="search-input"></dt-input>
              <dt-input label="Organization" placeholder="ASCA" data-testid="organization-filter"></dt-input>
              <dt-input label="Sport" placeholder="StockDog" data-testid="sport-filter"></dt-input>
              <dt-input label="Date From" type="date" data-testid="date-from"></dt-input>
              <dt-input label="Date To" type="date" data-testid="date-to"></dt-input>
              <div class="flex items-end">
                <label class="flex h-11 w-full items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 text-sm text-neutral-700" data-testid="include-inactive-wrapper">
                  <input type="checkbox" class="h-4 w-4 rounded border-neutral-300 text-primary-500" data-testid="include-inactive" ${this.draftFilters.includeInactive ? 'checked' : ''} />
                  <span>Include inactive trial events</span>
                </label>
              </div>
            </div>
            <div class="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div class="text-xs text-neutral-500">Filters are applied server-side with paginated admin results.</div>
              <div class="flex flex-wrap items-center gap-2">
                <dt-button variant="ghost" data-testid="clear-filters">Clear</dt-button>
                <dt-button variant="primary" data-testid="apply-filters">Apply filters</dt-button>
              </div>
            </div>
          </dt-card>

          ${body}
        </div>
      </section>
    `;
  }

  private renderEntryWorkflowView(): string {
    return `
      <dt-secretary-entry-list-view data-testid="secretary-entry-workflow"></dt-secretary-entry-list-view>
    `;
  }

  private renderForbidden(): string {
    return `
      <section class="px-4 py-12" data-testid="forbidden-state">
        <div class="mx-auto max-w-xl overflow-hidden rounded-[28px] border border-neutral-200 bg-white px-6 py-10 text-center shadow-[0_18px_48px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark">
          <div class="inline-flex items-center rounded-full bg-warning-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-warning-700 dark:bg-warning-500/15 dark:text-warning-200">Restricted</div>
          <h2 class="mt-4 text-2xl font-semibold text-neutral-900 dark:text-white">Access restricted</h2>
          <p class="mt-3 text-sm leading-7 text-neutral-600 dark:text-neutral-300">Only Secretary and AppAdmin can use the shared trial-event management surface.</p>
        </div>
      </section>
    `;
  }

  private updateRender(): void {
    let content = '';

    if (this.pageState.get() === 'forbidden') {
      content = this.renderForbidden();
    } else if (this.isEntryWorkflowView()) {
      content = this.renderEntryWorkflowView();
    } else {
      content = this.renderManagementView();
    }

    this.render(content);
    this.attachEventListeners();
    this.assignData();
  }

  private attachEventListeners(): void {
    this.$('[data-new-event]')?.addEventListener('click', event => {
      event.preventDefault();
      navigate('/secretary/trial-events/new');
    });
    if (this.isEntryWorkflowView() || this.pageState.get() === 'forbidden') {
      return;
    }

    const retryBtn = this.$('[data-testid="retry-btn"]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.handleRetry);
    }

    const applyBtn = this.$('[data-testid="apply-filters"]');
    if (applyBtn) {
      applyBtn.addEventListener('click', this.handleApplyFilters);
    }

    const clearBtn = this.$('[data-testid="clear-filters"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', this.handleClearFilters);
    }

    const searchInput = this.$('[data-testid="search-input"]');
    if (searchInput) {
      searchInput.addEventListener('input', this.handleSearchInput);
    }

    const organizationInput = this.$('[data-testid="organization-filter"]');
    if (organizationInput) {
      organizationInput.addEventListener('input', this.handleOrganizationInput);
    }

    const sportInput = this.$('[data-testid="sport-filter"]');
    if (sportInput) {
      sportInput.addEventListener('input', this.handleSportInput);
    }

    const dateFromInput = this.$('[data-testid="date-from"]');
    if (dateFromInput) {
      dateFromInput.addEventListener('input', this.handleDateFromInput);
    }

    const dateToInput = this.$('[data-testid="date-to"]');
    if (dateToInput) {
      dateToInput.addEventListener('input', this.handleDateToInput);
    }

    const includeInactive = this.$('[data-testid="include-inactive"]');
    if (includeInactive) {
      includeInactive.addEventListener('change', this.handleIncludeInactiveChange);
    }

    this.$$('[data-testid="open-trial-event"]').forEach((button) => {
      button.addEventListener('click', this.handleOpenTrialEvent);
    });

    this.$$('[data-testid="manage-entries"]').forEach((button) => {
      button.addEventListener('click', this.handleManageEntries);
    });

    const prevBtn = this.$('[data-testid="prev-page"]');
    if (prevBtn) {
      prevBtn.addEventListener('click', this.handlePrevPage);
    }

    const nextBtn = this.$('[data-testid="next-page"]');
    if (nextBtn) {
      nextBtn.addEventListener('click', this.handleNextPage);
    }

    const pageSizeSelect = this.$('[data-testid="page-size"]') as DtSelectElement | null;
    if (pageSizeSelect) {
      pageSizeSelect.addEventListener('change', this.handlePageSizeChange);
    }
  }

  private assignData(): void {
    if (this.pageState.get() === 'forbidden') {
      return;
    }

    if (this.isEntryWorkflowView()) {
      const entryView = this.$('dt-secretary-entry-list-view') as SecretaryEntryListViewElement | null;
      if (entryView) {
        const trialEventId = this.getSelectedTrialEventId();
        const trialName = this.getSelectedTrialName();
        if (trialEventId) {
          entryView.setAttribute('trial-event-id', trialEventId);
        }
        if (trialName) {
          entryView.setAttribute('trial-name', trialName);
        }
      }
      return;
    }

    const searchInput = this.$('[data-testid="search-input"]') as InputElement | null;
    if (searchInput) {
      searchInput.value = this.draftFilters.search;
    }

    const organizationInput = this.$('[data-testid="organization-filter"]') as InputElement | null;
    if (organizationInput) {
      organizationInput.value = this.draftFilters.organizationCode;
    }

    const sportInput = this.$('[data-testid="sport-filter"]') as InputElement | null;
    if (sportInput) {
      sportInput.value = this.draftFilters.sportCode;
    }

    const dateFromInput = this.$('[data-testid="date-from"]') as InputElement | null;
    if (dateFromInput) {
      dateFromInput.value = this.draftFilters.dateFrom;
    }

    const dateToInput = this.$('[data-testid="date-to"]') as InputElement | null;
    if (dateToInput) {
      dateToInput.value = this.draftFilters.dateTo;
    }

    const includeInactive = this.$('[data-testid="include-inactive"]') as HTMLInputElement | null;
    if (includeInactive) {
      includeInactive.checked = this.draftFilters.includeInactive;
    }

    const prevBtn = this.$('[data-testid="prev-page"]') as DtButtonElement | null;
    const nextBtn = this.$('[data-testid="next-page"]') as DtButtonElement | null;
    if (prevBtn) {
      prevBtn.disabled = this.page.get() <= 1 || this.loading.get();
    }
    if (nextBtn) {
      nextBtn.disabled = this.page.get() >= this.getTotalPages() || this.loading.get();
    }

    const pageSizeSelect = this.$('[data-testid="page-size"]') as DtSelectElement | null;
    if (pageSizeSelect) {
      pageSizeSelect.options = [
        { value: '10', label: '10' },
        { value: '25', label: '25' },
        { value: '50', label: '50' },
      ];
      pageSizeSelect.value = this.pageSize.get().toString();
      pageSizeSelect.disabled = this.loading.get();
    }
  }
}

customElements.define('dt-secretary-page', SecretaryListPage);
